import {
	type Procedure,
	type ProcedureInitiative,
	type ProcedureStep,
	type ProcedureStepFields,
	type Stored,
} from "@overmap-ai/common"
import {
	selectAssetsMapping,
	selectLatestFieldsByProcedureStep,
	selectProceduresOfProcedureInitiative,
	selectProcedureStepFields,
	selectProcedureStepsOfProcedureType,
} from "@overmap-ai/core"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"
import { useMemo } from "react"

import { ProcedureStepCompletionStatus } from "../enums/procedures"
import { useAppSelector, useShallowEqaulSelector } from "../store"
import {
	selectAssetTypeIdentifierValuesByAsset,
	selectAttachmentsByAssetTypeFieldValues,
	selectAttachmentsByProcedureFieldValues,
	selectAttachmentsByProcedureStepFieldValues,
	selectLatestAssetTypeFieldValuesByAsset,
	selectLatestProcedureFieldValuesByProcedure,
	selectLatestProcedureStepFieldValuesByProcedure,
	selectLatestProcedureStepFieldValuesReviewByFieldValues,
	selectProcedureStepAssigneesByProcedure,
	selectProcedureStepReviewersByProcedure,
} from "../store/selectors"
import type {
	ProcedureCompletionData,
	ProcedureData,
	ProcedureInitiativeData,
	ProcedureStepCompletionData,
} from "../typings"
import { getProcedureStepCompletionStatus } from "../utils/procedures"

export const useProcedureData = (procedures: Stored<Procedure>[]) => {
	const procedureStepAssigneesByProcedure = useAppSelector(selectProcedureStepAssigneesByProcedure)
	const procedureStepReviewersByProcedure = useAppSelector(selectProcedureStepReviewersByProcedure)
	const latestProcedureFieldValuesByProcedure = useAppSelector(selectLatestProcedureFieldValuesByProcedure)
	const attachmentsByProcedureFieldValues = useAppSelector(selectAttachmentsByProcedureFieldValues)
	const latestProcedureStepFieldValuesByProcedure = useAppSelector(selectLatestProcedureStepFieldValuesByProcedure)
	const attachmentsByProcedureStepFieldValues = useAppSelector(selectAttachmentsByProcedureStepFieldValues)
	const latestProcedureStepFieldValuesReviewByFieldValues = useAppSelector(
		selectLatestProcedureStepFieldValuesReviewByFieldValues,
	)

	return useMemo(() => {
		const ret: ProcedureData[] = []

		for (const procedure of procedures) {
			const procedureFieldValues = latestProcedureFieldValuesByProcedure[procedure.uuid]
			const procedureFieldValuesAttachments =
				attachmentsByProcedureFieldValues[procedureFieldValues?.uuid ?? ""] ?? []
			const procedureStepAssignees = procedureStepAssigneesByProcedure[procedure.uuid] ?? {}
			const procedureStepReviewers = procedureStepReviewersByProcedure[procedure.uuid] ?? {}
			const procedureStepFieldValues = latestProcedureStepFieldValuesByProcedure[procedure.uuid] ?? {}
			const procedureStepFieldValuesAttachments: ProcedureData["procedureStepFieldValuesAttachments"] = {}
			const procedureStepFieldValuesReviews: ProcedureData["procedureStepFieldValuesReviews"] = {}

			for (const [procedureStepId, fieldValues] of Object.entries(procedureStepFieldValues)) {
				if (!fieldValues) continue

				procedureStepFieldValuesReviews[procedureStepId] =
					latestProcedureStepFieldValuesReviewByFieldValues[fieldValues.uuid]
				procedureStepFieldValuesAttachments[procedureStepId] =
					attachmentsByProcedureStepFieldValues[fieldValues.uuid]
			}

			ret.push({
				procedure,
				procedureFieldValues,
				procedureFieldValuesAttachments,
				procedureStepFieldValues,
				procedureStepFieldValuesAttachments,
				procedureStepFieldValuesReviews,
				procedureStepAssignees,
				procedureStepReviewers,
			})
		}

		return ret
	}, [
		procedures,
		latestProcedureFieldValuesByProcedure,
		latestProcedureStepFieldValuesByProcedure,
		latestProcedureStepFieldValuesReviewByFieldValues,
		procedureStepAssigneesByProcedure,
		procedureStepReviewersByProcedure,
		attachmentsByProcedureStepFieldValues,
		attachmentsByProcedureFieldValues,
	])
}

export const useFieldSchemasAndConditionsOfProcedureSteps = (procedureSteps: Stored<ProcedureStep>[]) => {
	const latestFieldsByProcedureStep = useShallowEqaulSelector(selectLatestFieldsByProcedureStep)

	return useMemo(() => {
		const ret: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]> = {}

		for (const procedureStep of procedureSteps) {
			const latestFields = latestFieldsByProcedureStep[procedureStep.uuid]
			const fieldSchema = FieldSchema.deserialize(latestFields?.fields ?? [])
			const conditions = fieldSchema.deserializeConditions(latestFields?.field_conditions ?? [])
			const conditionManager = new ConditionManager(conditions)
			ret[procedureStep.uuid] = [fieldSchema, conditionManager]
		}

		return ret
	}, [procedureSteps, latestFieldsByProcedureStep])
}

export const useFieldSchemasAndConditionsByProcedureStepFields = () => {
	const procedureStepFields = useAppSelector(selectProcedureStepFields)

	return useMemo(() => {
		const ret: Record<ProcedureStepFields["uuid"], [FieldSchema, ConditionManager]> = {}

		for (const fields of procedureStepFields) {
			const fieldSchema = FieldSchema.deserialize(fields.fields)
			const conditions = fieldSchema.deserializeConditions(fields.field_conditions ?? [])
			const conditionManager = new ConditionManager(conditions)
			ret[fields.uuid] = [fieldSchema, conditionManager]
		}

		return ret
	}, [procedureStepFields])
}

export const useProcedureIntiativeCompletionData = (procedureInitiative: Stored<ProcedureInitiative>) => {
	const procedures = useShallowEqaulSelector(selectProceduresOfProcedureInitiative(procedureInitiative.uuid))
	const procedureSteps = useShallowEqaulSelector(
		selectProcedureStepsOfProcedureType(procedureInitiative.procedure_type),
	)
	const latestProcedureStepFieldValuesByProcedure = useAppSelector(selectLatestProcedureStepFieldValuesByProcedure)
	const fieldSchemasAndConditionsByProcedureStepFields = useFieldSchemasAndConditionsByProcedureStepFields()

	return useMemo(() => {
		const ret: Record<ProcedureStep["uuid"], ProcedureStepCompletionData> = {}

		for (const { uuid } of procedureSteps) {
			ret[uuid] = {
				[ProcedureStepCompletionStatus.COMPLETE]: 0,
				[ProcedureStepCompletionStatus.FAILED]: 0,
				[ProcedureStepCompletionStatus.INCOMPLETE]: 0,
			}
		}

		for (const procedure of procedures) {
			const procedureStepFieldValuesMapping = latestProcedureStepFieldValuesByProcedure[procedure.uuid] ?? {}

			for (const procedureStep of procedureSteps) {
				const procedureStepFieldValues = procedureStepFieldValuesMapping[procedureStep.uuid]

				let status: ProcedureStepCompletionStatus | undefined

				if (
					!procedureStepFieldValues ||
					!(procedureStepFieldValues.fields_revision in fieldSchemasAndConditionsByProcedureStepFields)
				) {
					status = ProcedureStepCompletionStatus.INCOMPLETE
				} else {
					const [fieldSchema, conditionManager] =
						fieldSchemasAndConditionsByProcedureStepFields[procedureStepFieldValues.fields_revision]

					status = getProcedureStepCompletionStatus(
						fieldSchema,
						conditionManager,
						procedureStepFieldValues.values,
						!procedureStepFieldValues.published_at,
					)
				}

				ret[procedureStep.uuid][status] += 1
			}
		}

		return ret
	}, [
		fieldSchemasAndConditionsByProcedureStepFields,
		procedureSteps,
		procedures,
		latestProcedureStepFieldValuesByProcedure,
	])
}

export const useProcedureCompletionData = (procedure: Stored<Procedure>) => {
	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedure.procedure_type))
	const latestProcedureStepFieldValuesByProcedure = useAppSelector(selectLatestProcedureStepFieldValuesByProcedure)
	const latestProcedureStepFieldValuesReviewByFieldValues = useAppSelector(
		selectLatestProcedureStepFieldValuesReviewByFieldValues,
	)

	const fieldSchemasAndConditionsByProcedureStepFields = useFieldSchemasAndConditionsByProcedureStepFields()

	return useMemo<ProcedureCompletionData>(() => {
		const completionStatuses: ProcedureCompletionData["completionStatuses"] = {}
		const reviewTypes: ProcedureCompletionData["reviewTypes"] = {}
		const completionStatusCounts: ProcedureCompletionData["completionStatusCounts"] = {
			[ProcedureStepCompletionStatus.COMPLETE]: 0,
			[ProcedureStepCompletionStatus.FAILED]: 0,
			[ProcedureStepCompletionStatus.INCOMPLETE]: 0,
		}

		const fieldValuesByProcedureStep = latestProcedureStepFieldValuesByProcedure[procedure.uuid]

		for (const procedureStep of procedureSteps) {
			const procedureStepFieldValues = fieldValuesByProcedureStep?.[procedureStep.uuid]
			const procedureStepFieldValuesReview =
				latestProcedureStepFieldValuesReviewByFieldValues[procedureStepFieldValues?.uuid ?? ""]

			let status: ProcedureStepCompletionStatus | undefined = undefined

			if (
				!procedureStepFieldValues ||
				!(procedureStepFieldValues.fields_revision in fieldSchemasAndConditionsByProcedureStepFields)
			) {
				status = ProcedureStepCompletionStatus.INCOMPLETE
			} else {
				const [fieldSchema, conditionManager] =
					fieldSchemasAndConditionsByProcedureStepFields[procedureStepFieldValues.fields_revision]

				status = getProcedureStepCompletionStatus(
					fieldSchema,
					conditionManager,
					procedureStepFieldValues.values,
					!procedureStepFieldValues?.published_at,
				)
			}

			const reviewType = procedureStepFieldValuesReview?.type
			completionStatuses[procedureStep.uuid] = status
			reviewTypes[procedureStep.uuid] = reviewType
			completionStatusCounts[status] += 1
		}

		return {
			completionStatuses,
			reviewTypes,
			completionStatusCounts,
		}
	}, [
		procedureSteps,
		fieldSchemasAndConditionsByProcedureStepFields,
		procedure.uuid,
		latestProcedureStepFieldValuesByProcedure,
		latestProcedureStepFieldValuesReviewByFieldValues,
	])
}

export const useProcedureInitiativeData = (procedureInitiativeId: ProcedureInitiative["uuid"]) => {
	const procedures = useShallowEqaulSelector(selectProceduresOfProcedureInitiative(procedureInitiativeId))
	const assetsById = useAppSelector(selectAssetsMapping)
	const latestAssetTypeFieldValuesByAsset = useAppSelector(selectLatestAssetTypeFieldValuesByAsset)
	const attachmentsByAssetTypeFieldValues = useAppSelector(selectAttachmentsByAssetTypeFieldValues)
	const assetTypeIdentifierValuesByAsset = useAppSelector(selectAssetTypeIdentifierValuesByAsset)
	const latestProcedureFieldValuesByProcedure = useAppSelector(selectLatestProcedureFieldValuesByProcedure)
	const procedureStepAssigneesByProcedure = useAppSelector(selectProcedureStepAssigneesByProcedure)
	const procedureStepReviewersByProcedure = useAppSelector(selectProcedureStepReviewersByProcedure)
	const latestProcedureStepFieldValuesByProcedure = useAppSelector(selectLatestProcedureStepFieldValuesByProcedure)
	const latestProcedureStepFieldValuesReviewByFieldValues = useAppSelector(
		selectLatestProcedureStepFieldValuesReviewByFieldValues,
	)
	const attachmentsByProcedureFieldValues = useAppSelector(selectAttachmentsByProcedureFieldValues)
	const attachmentsByProcedureStepFieldValues = useAppSelector(selectAttachmentsByProcedureStepFieldValues)

	return useMemo(() => {
		const ret: ProcedureInitiativeData[] = []

		for (const procedure of procedures) {
			const asset = assetsById[procedure.asset]

			if (!asset) {
				throw new Error(`Asset ${procedure.asset} of procedure ${procedure.uuid} not found in store.`)
			}

			const latestAssetTypeFieldValues = latestAssetTypeFieldValuesByAsset[asset.uuid]
			const assetTypeFieldValuesAttachments =
				attachmentsByAssetTypeFieldValues[latestAssetTypeFieldValues?.uuid ?? ""] ?? []
			const latestProcedureFieldValues = latestProcedureFieldValuesByProcedure[procedure.uuid]
			const procedureFieldValuesAttachments =
				attachmentsByProcedureFieldValues[latestProcedureFieldValues?.uuid ?? ""] ?? []
			const latestProcedureStepFieldValues = latestProcedureStepFieldValuesByProcedure[procedure.uuid] ?? {}
			const procedureStepFieldValuesAttachments: ProcedureData["procedureStepFieldValuesAttachments"] = {}
			const latestProcedureStepFieldValuesReviews: ProcedureData["procedureStepFieldValuesReviews"] = {}

			for (const [procedureStepId, fieldValues] of Object.entries(latestProcedureStepFieldValues)) {
				if (!fieldValues) continue

				latestProcedureStepFieldValuesReviews[procedureStepId] =
					latestProcedureStepFieldValuesReviewByFieldValues[fieldValues.uuid]
				procedureStepFieldValuesAttachments[procedureStepId] =
					attachmentsByProcedureStepFieldValues[fieldValues.uuid] ?? []
			}

			ret.push({
				procedure,
				asset,
				assetTypeFieldValues: latestAssetTypeFieldValues,
				assetTypeFieldValuesAttachments,
				assetTypeIdentifierValues: assetTypeIdentifierValuesByAsset[asset.uuid] ?? {},
				procedureFieldValues: latestProcedureFieldValues,
				procedureFieldValuesAttachments,
				procedureStepFieldValues: latestProcedureStepFieldValues,
				procedureStepFieldValuesAttachments,
				procedureStepFieldValuesReviews: latestProcedureStepFieldValuesReviews,
				procedureStepAssignees: procedureStepAssigneesByProcedure[procedure.uuid] ?? {},
				procedureStepReviewers: procedureStepReviewersByProcedure[procedure.uuid] ?? {},
			})
		}

		return ret
	}, [
		assetsById,
		latestAssetTypeFieldValuesByAsset,
		attachmentsByAssetTypeFieldValues,
		attachmentsByProcedureFieldValues,
		attachmentsByProcedureStepFieldValues,
		assetTypeIdentifierValuesByAsset,
		procedures,
		latestProcedureFieldValuesByProcedure,
		procedureStepAssigneesByProcedure,
		latestProcedureStepFieldValuesByProcedure,
		latestProcedureStepFieldValuesReviewByFieldValues,
		procedureStepReviewersByProcedure,
	])
}
